import React, { useState, useMemo, useEffect } from 'react';
import { Row, Col, Button, DatePicker, Select, Card, Statistic, Table, Alert, Spin, Empty } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import type { Dayjs } from 'dayjs';
import PageContainer from '@/components/PageContainer';
import ChartCard from '@/components/ChartCard';
import Loading from '@/components/Loading';
import { useInterProvincePnl } from '@/api/interProvincePnl';
import { useStations } from '@/hooks/useStations';
import { format2 } from '@/utils/charts';
import type { EChartsOption } from 'echarts';

const { RangePicker } = DatePicker;

const InterProvincePnlPage: React.FC = () => {
  const { data: stationsData, isLoading: stationsLoading } = useStations();
  const stations = stationsData?.stations ?? [];

  const today = dayjs();
  const yesterday = today.subtract(1, 'day');

  const [selectedStation, setSelectedStation] = useState('');
  const [dates, setDates] = useState<[Dayjs, Dayjs]>([yesterday, yesterday]);

  useEffect(() => {
    if (stations.length > 0 && !selectedStation) {
      setSelectedStation(stations[0]);
    }
  }, [stations, selectedStation]);

  const startDateStr = dates[0].format('YYYY-MM-DD');
  const endDateStr = dates[1].format('YYYY-MM-DD');
  const isSingleDay = startDateStr === endDateStr;

  const pnlParams = useMemo(() => ({
    station: selectedStation,
    start_date: startDateStr,
    end_date: endDateStr,
  }), [selectedStation, startDateStr, endDateStr]);

  const { data: response, isLoading, error } = useInterProvincePnl(pnlParams);
  const data = response?.data;

  // ── Province chart option (single day) ──
  const provinceChartOption: EChartsOption = useMemo(() => {
    const curve = data?.province_curve;
    if (!curve || curve.length === 0) {
      return {
        title: { text: '暂无数据', left: 'center', textStyle: { color: '#9ca3af' } },
      };
    }
    return {
      tooltip: {
        trigger: 'axis',
        axisPointer: { type: 'cross' },
        formatter: (params: any) => {
          if (!Array.isArray(params)) return '';
          let html = `<div style="font-weight:600;margin-bottom:4px">${params[0].axisValue}</div>`;
          params.forEach((p: any) => {
            const unit = p.seriesName.includes('电价') ? '元/MWh' : 'MW';
            html += `<div>${p.marker} ${p.seriesName}: <strong>${format2(p.value)}</strong> ${unit}</div>`;
          });
          return html;
        },
      },
      legend: {
        data: ['全省中标电价', '全省中标电量'],
        top: 4,
      },
      grid: { left: 60, right: 60, top: 48, bottom: 40 },
      xAxis: {
        type: 'category',
        data: curve.map((p) => p.time_label),
        axisLabel: { color: '#9ca3af', fontSize: 10, interval: 7 },
      },
      yAxis: [
        {
          type: 'value',
          name: '电价 (元/MWh)',
          position: 'left',
          axisLabel: { color: '#9ca3af', fontSize: 11 },
        },
        {
          type: 'value',
          name: '电量 (MW)',
          position: 'right',
          axisLabel: { color: '#9ca3af', fontSize: 11 },
        },
      ],
      series: [
        {
          name: '全省中标电价',
          type: 'line',
          yAxisIndex: 0,
          data: curve.map((p) => p.price),
          smooth: true,
          symbol: 'none',
          lineStyle: { color: '#f5222d', width: 2 },
        },
        {
          name: '全省中标电量',
          type: 'bar',
          yAxisIndex: 1,
          data: curve.map((p) => p.volume),
          barMaxWidth: 6,
          itemStyle: { color: '#1890ff', opacity: 0.6 },
        },
      ],
    };
  }, [data]);

  // ── Station chart option (single day) ──
  const stationChartOption: EChartsOption = useMemo(() => {
    const curve = data?.station_curve;
    if (!curve || curve.length === 0) {
      return {
        title: { text: '暂无数据', left: 'center', textStyle: { color: '#9ca3af' } },
      };
    }
    return {
      tooltip: {
        trigger: 'axis',
        axisPointer: { type: 'cross' },
        formatter: (params: any) => {
          if (!Array.isArray(params)) return '';
          let html = `<div style="font-weight:600;margin-bottom:4px">${params[0].axisValue}</div>`;
          params.forEach((p: any) => {
            const unit = p.seriesName.includes('电价') ? '元/MWh' : 'MWh';
            html += `<div>${p.marker} ${p.seriesName}: <strong>${format2(p.value)}</strong> ${unit}</div>`;
          });
          return html;
        },
      },
      legend: {
        data: ['省间电价', '节点电价', '中标电量'],
        top: 4,
      },
      grid: { left: 60, right: 60, top: 48, bottom: 40 },
      xAxis: {
        type: 'category',
        data: curve.map((p) => p.time_label),
        axisLabel: { color: '#9ca3af', fontSize: 10, interval: 7 },
      },
      yAxis: [
        {
          type: 'value',
          name: '电价 (元/MWh)',
          position: 'left',
          axisLabel: { color: '#9ca3af', fontSize: 11 },
        },
        {
          type: 'value',
          name: '电量 (MWh)',
          position: 'right',
          axisLabel: { color: '#9ca3af', fontSize: 11 },
        },
      ],
      series: [
        {
          name: '省间电价',
          type: 'line',
          yAxisIndex: 0,
          data: curve.map((p) => p.interprov_price),
          smooth: true,
          symbol: 'none',
          lineStyle: { color: '#f5222d', width: 2 },
        },
        {
          name: '节点电价',
          type: 'line',
          yAxisIndex: 0,
          data: curve.map((p) => p.node_price),
          smooth: true,
          symbol: 'none',
          lineStyle: { color: '#52c41a', width: 2 },
        },
        {
          name: '中标电量',
          type: 'bar',
          yAxisIndex: 1,
          data: curve.map((p) => p.energy),
          barMaxWidth: 6,
          itemStyle: { color: '#faad14', opacity: 0.6 },
        },
      ],
    };
  }, [data]);

  // ── Period detail table columns ──
  const periodColumns = [
    { title: '时段', dataIndex: 'period', key: 'period', fixed: 'left' as const, width: 70 },
    { title: '中标电力(MW)', dataIndex: 'power', key: 'power', render: (v: number | null) => v != null ? format2(v) : '-' },
    { title: '省间价格', dataIndex: 'interprov_price', key: 'interprov_price', render: (v: number | null) => v != null ? format2(v) : '-' },
    { title: '节点电价', dataIndex: 'node_price', key: 'node_price', render: (v: number | null) => v != null ? format2(v) : '-' },
    { title: 'Pdiff', dataIndex: 'pdiff', key: 'pdiff', render: (v: number) => format2(v) },
    { title: '电量(MWh)', dataIndex: 'energy', key: 'energy', render: (v: number) => format2(v) },
    {
      title: '增收(元)',
      dataIndex: 'pnl',
      key: 'pnl',
      render: (v: number | null) => {
        if (v == null) return '-';
        return <span style={{ color: v >= 0 ? '#059669' : '#dc2626' }}>{v >= 0 ? '+' : ''}{format2(v)}</span>;
      },
    },
  ];

  // ── Daily summary table columns ──
  const dailyColumns = [
    { title: '日期', dataIndex: 'date', key: 'date', fixed: 'left' as const },
    { title: '中标电量(MWh)', dataIndex: 'energy', key: 'energy', render: (v: number) => format2(v) },
    { title: '加权均价(元/MWh)', dataIndex: 'weighted_price', key: 'weighted_price', render: (v: number | null) => v != null ? format2(v) : '-' },
    { title: '省间电费(元)', dataIndex: 'interprov_fee', key: 'interprov_fee', render: (v: number) => format2(v) },
    { title: '省内机会成本(元)', dataIndex: 'opportunity_cost', key: 'opportunity_cost', render: (v: number) => format2(v) },
    {
      title: '增收(元)',
      dataIndex: 'pnl',
      key: 'pnl',
      render: (v: number) => <span style={{ color: v >= 0 ? '#059669' : '#dc2626' }}>{v >= 0 ? '+' : ''}{format2(v)}</span>,
    },
    { title: '交易笔数', dataIndex: 'trade_count', key: 'trade_count' },
  ];

  // ── Pdiff cards ──
  const pdiffEntries = useMemo(() => {
    const mp = data?.month_pdiff_map ?? {};
    const mt = data?.month_mtd_map ?? {};
    return Object.entries(mp).map(([key, pdiff]) => ({
      key,
      pdiff,
      mtd: mt[key] ?? 0,
    }));
  }, [data]);

  const hasZeroSubsidy = (data?.subsidy_price ?? 0) === 0;

  if (stationsLoading) {
    return (
      <PageContainer title="省间现货测算">
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 300 }}>
          <Spin size="large" tip="正在加载场站列表..." />
        </div>
      </PageContainer>
    );
  }

  return (
    <PageContainer title="省间现货测算">
      {/* Control bar */}
      <div style={{ paddingBottom: 16, marginBottom: 24, borderBottom: '1px solid #e5e7eb' }}>
        <Row gutter={[16, 16]} align="middle">
          <Col xs={24} sm={12} md={6}>
            <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 4 }}>场站</div>
            <Select
              value={selectedStation}
              options={stations.map((s: string) => ({ value: s, label: s }))}
              onChange={setSelectedStation}
              style={{ width: '100%' }}
              showSearch
              filterOption={(input, option) =>
                (option?.label as string)?.toLowerCase().includes(input.toLowerCase())
              }
            />
          </Col>
          <Col xs={24} sm={12} md={8}>
            <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 4 }}>日期范围</div>
            <RangePicker
              value={dates}
              onChange={(vals) => {
                if (vals && vals[0] && vals[1]) {
                  setDates([vals[0], vals[1]]);
                }
              }}
              style={{ width: '100%' }}
              allowClear={false}
            />
          </Col>
          <Col xs={12} sm={6} md={4}>
            <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 4 }}>&nbsp;</div>
            <Button type="primary" icon={<SearchOutlined />} onClick={() => { /* React Query auto-refetches */ }}>
              查询
            </Button>
          </Col>
        </Row>

        {/* Pdiff info */}
        {pdiffEntries.length === 1 && (
          <div style={{ marginTop: 16, padding: '12px 16px', background: '#fff7ed', border: '1px solid #ffedd5', borderRadius: 10, fontSize: 13, color: '#c2410c' }}>
            场站补贴电价基数: <strong>{format2(data?.subsidy_price ?? 0)}</strong> 元/MWh ｜
            当月光伏MTD均价: <strong>{format2(pdiffEntries[0]?.mtd ?? 0)}</strong> 元/MWh ｜
            推算补贴电价: <strong>{format2(pdiffEntries[0]?.pdiff ?? 0)}</strong> 元/MWh
          </div>
        )}
        {pdiffEntries.length > 1 && (
          <Row gutter={[16, 8]} style={{ marginTop: 12 }}>
            {pdiffEntries.map(({ key, pdiff, mtd }) => (
              <Col key={key}>
                <Card size="small">
                  <Statistic title={key} value={format2(pdiff)} suffix="元/MWh" />
                  <div style={{ fontSize: 12, color: '#6b7280' }}>
                    MTD: {format2(mtd)} 元/MWh
                  </div>
                </Card>
              </Col>
            ))}
          </Row>
        )}
        {hasZeroSubsidy && (
          <Alert
            message="该场站在 station_master 中未配置 subsidy_price，Pdiff 将按 0 计算补贴电价基数。"
            type="warning"
            showIcon
            style={{ marginTop: 12 }}
          />
        )}
      </div>

      {/* Loading / Error */}
      {isLoading && <Loading />}
      {error && <Alert message="数据加载失败" description={String(error)} type="error" showIcon />}

      {/* Empty state */}
      {data && !data.has_data && !isLoading && (
        <Empty description={`${selectedStation} 在所选日期范围内无省间现货清分数据`} />
      )}

      {/* Data content */}
      {data && data.has_data && (
        <>
          {/* KPI Cards */}
          <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
            <Col xs={24} sm={8}>
              <Card>
                <Statistic
                  title={isSingleDay ? '中标电量' : '累计中标电量'}
                  value={format2(data.kpi.total_energy)}
                  suffix="MWh"
                />
                {!isSingleDay && (
                  <div style={{ fontSize: 12, color: '#6b7280', marginTop: 4 }}>
                    日均 {format2(data.kpi.daily_avg_energy)} MWh ｜ 交易 {data.kpi.trade_days} 天
                  </div>
                )}
              </Card>
            </Col>
            <Col xs={24} sm={8}>
              <Card>
                <Statistic
                  title="加权均价"
                  value={format2(data.kpi.weighted_avg_price)}
                  suffix="元/MWh"
                />
              </Card>
            </Col>
            <Col xs={24} sm={8}>
              <Card>
                <Statistic
                  title={isSingleDay ? '预计增收' : '累计增收'}
                  value={data.kpi.total_pnl >= 0 ? `+${format2(data.kpi.total_pnl)}` : format2(data.kpi.total_pnl)}
                  suffix="元"
                  valueStyle={{ color: data.kpi.total_pnl >= 0 ? '#059669' : '#dc2626' }}
                />
                {!isSingleDay && (
                  <div style={{ fontSize: 12, color: '#6b7280', marginTop: 4 }}>
                    日均 {data.kpi.daily_avg_pnl >= 0 ? '+' : ''}{format2(data.kpi.daily_avg_pnl)} 元
                  </div>
                )}
              </Card>
            </Col>
          </Row>

          {/* Single Day Charts + Table */}
          {isSingleDay && (
            <>
              <ChartCard
                title={`山东全省分时段中标量价 — ${startDateStr} — 售方`}
                option={provinceChartOption}
                loading={false}
                height={400}
              />

              <div style={{ marginTop: 16 }}>
                <ChartCard
                  title={`${selectedStation} 中标量价 & 节点电价 — ${startDateStr}`}
                  option={stationChartOption}
                  loading={false}
                  height={400}
                />
              </div>

              <div style={{ marginTop: 16, background: '#fff', borderRadius: 8, border: '2px solid #d9d9d9', padding: 24 }}>
                <div style={{ fontSize: '0.9rem', fontWeight: 600, color: '#374151', marginBottom: 16 }}>逐时增收计算</div>
                <Table
                  dataSource={data.period_detail ?? []}
                  columns={periodColumns}
                  rowKey="period"
                  size="small"
                  scroll={{ x: 700, y: 500 }}
                  pagination={false}
                />
              </div>
            </>
          )}

          {/* Multi Day Table */}
          {!isSingleDay && (
            <div style={{ background: '#fff', borderRadius: 8, border: '2px solid #d9d9d9', padding: 24 }}>
              <div style={{ fontSize: '0.9rem', fontWeight: 600, color: '#374151', marginBottom: 16 }}>每日汇总</div>
              <Table
                dataSource={data.daily_summary}
                columns={dailyColumns}
                rowKey="date"
                size="small"
                scroll={{ x: 900 }}
                pagination={false}
                summary={() => {
                  const totalPnl = data.kpi.total_pnl;
                  return (
                    <Table.Summary.Row>
                      <Table.Summary.Cell index={0}><strong>合计</strong></Table.Summary.Cell>
                      <Table.Summary.Cell index={1}><strong>{format2(data.kpi.total_energy)}</strong></Table.Summary.Cell>
                      <Table.Summary.Cell index={2}><strong>{format2(data.kpi.weighted_avg_price)}</strong></Table.Summary.Cell>
                      <Table.Summary.Cell index={3}><strong>{format2(data.daily_summary.reduce((s, r) => s + r.interprov_fee, 0))}</strong></Table.Summary.Cell>
                      <Table.Summary.Cell index={4}><strong>{format2(data.daily_summary.reduce((s, r) => s + r.opportunity_cost, 0))}</strong></Table.Summary.Cell>
                      <Table.Summary.Cell index={5}>
                        <strong style={{ color: totalPnl >= 0 ? '#059669' : '#dc2626' }}>
                          {totalPnl >= 0 ? '+' : ''}{format2(totalPnl)}
                        </strong>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={6}><strong>{data.daily_summary.reduce((s, r) => s + r.trade_count, 0)}</strong></Table.Summary.Cell>
                    </Table.Summary.Row>
                  );
                }}
              />
            </div>
          )}
        </>
      )}
    </PageContainer>
  );
};

export default InterProvincePnlPage;
